<template>
  <section class="breakeven-percentage-for-bluffing-the-turn">
    <readonly-alert v-if="!authorized" />
    <saved-scenario-alert
      v-if="savedScenario"
      v-bind:title="savedScenario.title"
    />
    <header class="page-header">
      <h1>
        Breakeven percentage for bluffing the turn
        <router-link to="/help/breakeven-percentage-for-bluffing-the-turn">
          <img src="/static/help.png" width="50">
        </router-link>
      </h1>
    </header>
    <section class="row">
      <section class="col-sm-6">
        <breakeven-percentage-for-bluffing-the-turn-inputs
          v-bind:state="state"
          v-bind:authorized="authorized"
        />
        <section class="hidden-xs">
          <hr>
          <create-sharable-url-button
            v-bind:disabled="!authorized"
            v-on:createSharableUrl="$emit('createSharableUrl', {
              method: 'createSharableTurnBluffBreakevenPercentageURL',
              state: state,
            })"
          />
          <save-scenario-button
            v-bind:savedScenario="savedScenario"
            v-bind:disabled="!authorized"
            v-on:saveScenario="$emit('saveScenario', {
              method: 'createSharableTurnBluffBreakevenPercentageURL',
              savedScenarioPropertyName: 'turnBluffBreakevenPercentage',
              state: state,
            })"
            v-on:updateScenario="$emit('updateScenario', {
              method: '_constructTurnBluffBreakevenPercentageData',
              state: state,
            })"
          />
        </section>
      </section>
      <section class="col-sm-6">
        <p class="breakeven-percentage">Breakeven percentage: {{ getBreakevenPercentageString(state, state.turnBetSize) }}</p>
        <breakeven-percentage-for-bluffing-the-turn-graph
          v-bind:chartData="chartData"
        />
        <breakeven-percentage-for-bluffing-the-turn-table
          v-bind:breakevenPercentages="[
            getBreakevenPercentageString(state, state.turnBetSize),
            getBreakevenPercentageString(state, 33.3),
            getBreakevenPercentageString(state, 50),
            getBreakevenPercentageString(state, 66.6),
            getBreakevenPercentageString(state, 75),
            getBreakevenPercentageString(state, 100),
            getBreakevenPercentageString(state, 150),
            getBreakevenPercentageString(state, 200)
          ]"
          v-bind:dropoffPercentages="[
            getDropoffPercentageString(state, state.turnBetSize),
            getDropoffPercentageString(state, 33.3),
            getDropoffPercentageString(state, 50),
            getDropoffPercentageString(state, 66.6),
            getDropoffPercentageString(state, 75),
            getDropoffPercentageString(state, 100),
            getDropoffPercentageString(state, 150),
            getDropoffPercentageString(state, 200)
          ]"
        />
      </section>
    </section>
    <section class="visible-xs">
      <hr>
      <create-sharable-url-button
        v-bind:disabled="!authorized"
        v-on:createSharableUrl="$emit('createSharableUrl', {
          type: 'createSharableTurnBluffBreakevenPercentageURL',
          state: state,
        })"
      />
      <save-scenario-button
        v-bind:savedScenario="savedScenario"
        v-bind:disabled="!authorized"
        v-on:saveScenario="$emit('saveScenario', {
          method: 'createSharableTurnBluffBreakevenPercentageURL',
          savedScenarioPropertyName: 'turnBluffBreakevenPercentage',
          state: state,
        })"
        v-on:updateScenario="$emit('updateScenario', {
          method: '_constructTurnBluffBreakevenPercentageData',
          state: state,
        })"
      />
    </section>
    <onboarding-modal
      toolName="breakeven percentage for bluffing the turn tool"
      urlPath="breakeven-percentage-for-bluffing-the-turn"
    />
  </section>
</template>

<script>
import Vue from 'vue';
import ReadonlyAlert from '@/components/utilities/readonly-alert';
import SavedScenarioAlert from '@/components/utilities/saved-scenario-alert';
import BreakevenPercentageForBluffingTheTurnInputs from '@/components/breakeven-percentage-for-bluffing-the-turn/breakeven-percentage-for-bluffing-the-turn-inputs';
import BreakevenPercentageForBluffingTheTurnGraph from '@/components/breakeven-percentage-for-bluffing-the-turn/breakeven-percentage-for-bluffing-the-turn-graph';
import BreakevenPercentageForBluffingTheTurnTable from '@/components/breakeven-percentage-for-bluffing-the-turn/breakeven-percentage-for-bluffing-the-turn-table';
import CreateSharableUrlButton from '@/components/controls/create-sharable-url-button';
import SaveScenarioButton from '@/components/controls/save-scenario-button';
import OnboardingModal from '@/components/onboarding-modal';

export default {
  name: 'breakeven-percentage-for-bluffing-the-turn',
  components: {
    'readonly-alert': ReadonlyAlert,
    'saved-scenario-alert': SavedScenarioAlert,
    'breakeven-percentage-for-bluffing-the-turn-inputs': BreakevenPercentageForBluffingTheTurnInputs,
    'breakeven-percentage-for-bluffing-the-turn-graph': BreakevenPercentageForBluffingTheTurnGraph,
    'breakeven-percentage-for-bluffing-the-turn-table': BreakevenPercentageForBluffingTheTurnTable,
    'create-sharable-url-button': CreateSharableUrlButton,
    'save-scenario-button': SaveScenarioButton,
    'onboarding-modal': OnboardingModal,
  },
  data: function () {
    return {
      chartLabels: [ 0,5,10,15,20,25,30,35,40,45,50,55,60,65,70,75,80,85,90,95,100 ],
    };
  },
  computed: {
    state: function () {
      return this.$store.state.breakevenPercentageForBluffingTheTurn;
    },
    savedScenario: function () {
      let routeId = this.$route.params.id;

      if (!this.authorized) {
        return false;
      }

      return this.$store.state.account.user.savedScenarios.turnBluffBreakevenPercentage.find(function (savedScenario) {
        return savedScenario.url.includes(routeId);
      });
    },
    chartData: function () {
      return {
        labels: this.chartLabels,
        datasets: [
          {
            label: 'EV of bluffing',
            data: this.evOfBluffingData,
            fill: false,
            borderColor: 'rgb(75, 192, 192)',
            lineTension: 0.1
          }, {
            label: 'EV of checking',
            data: this.evOfCheckingData,
            fill: false,
            borderColor: 'gray',
            lineTension: 0.1
          }
        ],
      };
    },
    evOfBluffingData: function () {
      return this.chartLabels.map(function (turnFoldPercentage) {
        return this.calculateEVOfBluffing(this.state, turnFoldPercentage);
      }, this);
    },
    evOfCheckingData: function () {
      return this.chartLabels.map(function () {
        return this.calculateEVOfChecking(this.state);
      }, this);
    },
    authorized: function () {
      return this.$store.getters['account/authorizedForPremium'];
    },
  },
  mounted: function () {
    if (this.$route.params.id) {
      this.load();
    }

    this.handleOnboard();
  },
  methods: {
    load: function () {
      let self = this;

      self.$http.get(`${process.env.BASE_API_URL}/sharable-url-store/` + self.$route.params.id)
        .then(function (response) {
          if (!response || !response.data.storeState) {
            alert('Didn\'t get data back from server. Redirecting to the default breakeven percentage for bluffing the turn page.');
            self.$router.push('/breakeven-percentage-for-bluffing-the-turn');
          } else {
            self.$store.commit('breakevenPercentageForBluffingTheTurn/setState', response.data.storeState);
          }
        })
        .catch(function () {
          alert('URL doesn\'t exist. Redirecting to the default breakeven percentage for bluffing the turn page.');
          self.$router.push('/breakeven-percentage-for-bluffing-the-turn');
        })
      ;
    },
    handleOnboard: function () {
      let self = this;

      if (
        !self.authorized ||
        !self.$store.state.account.user.settings.onboarding ||
        self.$store.state.account.user.settings.onboarding.breakevenPercentageForBluffingTheTurn
      ) {
        return;
      }

      $(self.$el).find('.onboarding-modal').modal();
      self.$http
        .patch(`${process.env.BASE_API_URL}/user/onboard`, { page: 'breakevenPercentageForBluffingTheTurn' })
        .then(function () {
          self.$store.commit('account/onboard', 'breakevenPercentageForBluffingTheTurn');
        })
        .catch(function () {
          console.log('Error: Unable to onboard.');
        })
      ;
    },

    calculateEVOfBluffing: function (state, turnFoldPercentage) {
      let bluffingEV_immediateFold = this._bluffingEV_immediateFold(state, turnFoldPercentage);
      let bluffingEV_blankRiver = this._bluffingEV_blankRiver(state, turnFoldPercentage);
      let bluffingEV_hitAnOut = this._bluffingEV_hitAnOut(state, turnFoldPercentage);
      let bluffingEV_scareCardAndWin = this._bluffingEV_scareCardAndWin(state, turnFoldPercentage);
      let bluffingEV_scareCardAndLose = this._bluffingEV_scareCardAndLose(state, turnFoldPercentage);

      return (
        bluffingEV_immediateFold +
        bluffingEV_blankRiver +
        bluffingEV_hitAnOut +
        bluffingEV_scareCardAndWin +
        bluffingEV_scareCardAndLose
      ).roundToTwoDecimalPlaces();
    },
    _bluffingEV_immediateFold: function (state, turnFoldPercentage) {
      return (turnFoldPercentage / 100) * state.turnPotSize;
    },
    _bluffingEV_blankRiver: function (state, turnFoldPercentage) {
      let probabilityOfTurnCall = (1 - (turnFoldPercentage / 100));
      let probabilityOfBlankRiver = (1 - (state.numberOfOuts / 46) - (state.numRiverCardsYoullBluff / 46));
      let turnBet = (state.turnPotSize * (state.turnBetSize / 100));

      return probabilityOfTurnCall * probabilityOfBlankRiver * -1 * turnBet;
    },
    _bluffingEV_hitAnOut: function (state, turnFoldPercentage) {
      let probabilityOfTurnCall = (1 - (turnFoldPercentage / 100));
      let turnBet = (state.turnPotSize * (state.turnBetSize / 100));
      let riverSize = state.turnPotSize + (2 * turnBet);
      let riverBet = (state.percentageOfPotWonOnRiverWhenYouHitAnOut / 100) * riverSize;
      let amountWon = state.turnPotSize + turnBet + riverBet;

      return probabilityOfTurnCall * (state.numberOfOuts / 46) * amountWon;
    },
    _bluffingEV_scareCardAndWin: function (state, turnFoldPercentage) {
      let probabilityOfTurnCall = (1 - (turnFoldPercentage / 100));
      let probabilityOfRiverFold = (state.probabilityOfGettingFoldOnRiver / 100);
      let turnBet = (state.turnPotSize * (state.turnBetSize / 100));
      let amountWon = state.turnPotSize + turnBet;

      return probabilityOfTurnCall * (state.numRiverCardsYoullBluff / 46) * probabilityOfRiverFold * amountWon;
    },
    _bluffingEV_scareCardAndLose: function (state, turnFoldPercentage) {
      let probabilityOfTurnCall = (1 - (turnFoldPercentage / 100));
      let probabilityOfRiverCall = 1 - (state.probabilityOfGettingFoldOnRiver / 100);
      let turnBet = (state.turnPotSize * (state.turnBetSize / 100));
      let riverSize = state.turnPotSize + (2 * turnBet);
      let riverBet = (state.riverBluffSize / 100) * riverSize;
      let amountLost = turnBet + riverBet;

      return probabilityOfTurnCall * (state.numRiverCardsYoullBluff / 46) * probabilityOfRiverCall * amountLost * -1;
    },
    calculateEVOfChecking: function (state) {
      if (state.position === 'in position') {
        return this._calculateEVOfCheckingIP(state).roundToTwoDecimalPlaces();
      } else if (state.position === 'out of position') {
        return this._calculateEVOfCheckingOOP(state).roundToTwoDecimalPlaces();
      }
    },
    _calculateEVOfCheckingIP: function (state) {
      let riverBet = (state.turnPotSize * (state.percentageOfPotWonAfterTurnChecksThrough / 100));
      let amountWon = state.turnPotSize + riverBet;

      return (state.numberOfOuts / 46) * amountWon;
    },
    _calculateEVOfCheckingOOP: function (state) {
      let evOfCheckingOOP = 0;

      evOfCheckingOOP += this.__checkingEV_OOP_check_hit(state);

      if (state.checkCall) {
        evOfCheckingOOP += this.__checkingEV_OOP_bet_call_hit(state);
        evOfCheckingOOP += this.__checkingEV_OOP_bet_call_miss(state);
      }

      return evOfCheckingOOP;
    },
    __checkingEV_OOP_check_hit: function (state) {
      let probabilityThatOpponentChecksBack = (1 - (state.probabilityThatOpponentBetsWhenYouCheck / 100));
      let probabilityOfHitting = (state.numberOfOuts / 46);
      let riverBet = (state.turnPotSize * (state.percentageOfPotWonAfterTurnChecksThrough / 100));
      let amountWon = (state.turnPotSize + riverBet);

      return probabilityThatOpponentChecksBack * probabilityOfHitting * amountWon;
    },
    __checkingEV_OOP_bet_call_hit: function (state) {
      let probabilityThatOpponentBetsWhenYouCheck = (state.probabilityThatOpponentBetsWhenYouCheck / 100);
      let turnBet = (state.turnPotSize * (state.opponentsTurnBetSize / 100));
      let riverSize = (state.turnPotSize + (turnBet * 2));
      let riverBet = (riverSize * (state.percentageOfPotWonAfterCheckCall / 100));
      let amountWon = state.turnPotSize + turnBet + riverBet;

      return probabilityThatOpponentBetsWhenYouCheck * (state.numberOfOuts / 46) * amountWon;
    },
    __checkingEV_OOP_bet_call_miss: function (state) {
      let probabilityThatOpponentBetsWhenYouCheck = (state.probabilityThatOpponentBetsWhenYouCheck / 100);
      let turnBet = (state.turnPotSize * (state.opponentsTurnBetSize / 100));
      let probabilityOfMissing = (1 - (state.numberOfOuts / 46));

      return probabilityThatOpponentBetsWhenYouCheck * probabilityOfMissing * turnBet * -1;
    },
    getBreakevenPercentage: function (state, turnBetSize) {
      let currEVOfBluffing;
      let currEVOfChecking;
      let stateCopy = {
        turnPotSize: state.turnPotSize,
        turnBetSize: state.turnBetSize,
        numberOfOuts: state.numberOfOuts,
        percentageOfPotWonOnRiverWhenYouHitAnOut: state.percentageOfPotWonOnRiverWhenYouHitAnOut,
        numRiverCardsYoullBluff: state.numRiverCardsYoullBluff,
        riverBluffSize: state.riverBluffSize,
        probabilityOfGettingFoldOnRiver: state.probabilityOfGettingFoldOnRiver,
        position: state.position,
        probabilityThatOpponentBetsWhenYouCheck: state.probabilityThatOpponentBetsWhenYouCheck,
        checkCall: state.checkCall,
        opponentsTurnBetSize: state.opponentsTurnBetSize,
        percentageOfPotWonAfterCheckCall: state.percentageOfPotWonAfterCheckCall,
        percentageOfPotWonAfterTurnChecksThrough: state.percentageOfPotWonAfterTurnChecksThrough,
      };

      stateCopy.turnBetSize = turnBetSize;

      for (let turnFoldPercentage = -100; turnFoldPercentage <= 100; turnFoldPercentage += 0.01) {
        currEVOfBluffing = this.calculateEVOfBluffing(stateCopy, turnFoldPercentage);
        currEVOfChecking = this.calculateEVOfChecking(stateCopy);

        if (Math.abs(currEVOfBluffing - currEVOfChecking) < 0.02) {
          if (turnFoldPercentage < 0) {
            return null;
          }

          return turnFoldPercentage.roundToOneDecimalPlace();
        }
      }

      return null;
    },
    getBreakevenPercentageString: function (state, turnBluffSize) {
      let breakevenPercentage = this.getBreakevenPercentage(state, turnBluffSize);

      if (breakevenPercentage === null) {
        return 'n/a';
      } else {
        return breakevenPercentage + '%';
      }
    },
    getDropoffPercentageString: function (state, turnBluffSize) {
      let profitableBreakevenPercentage;
      let immediatelyProfitableBreakevenPercentage;

      profitableBreakevenPercentage = this.getBreakevenPercentage(state, turnBluffSize);

      if (profitableBreakevenPercentage === null) {
        return 'n/a';
      }

      immediatelyProfitableBreakevenPercentage = (turnBluffSize / (turnBluffSize + 100)) * 100;

      return (immediatelyProfitableBreakevenPercentage - profitableBreakevenPercentage).roundToOneDecimalPlace() + '%';
    },
  },
};
</script>

<style lang="scss" scoped>
  .breakeven-percentage-for-bluffing-the-turn {
    font-size: 18px;

    .breakeven-percentage {
      text-align: center;
    }

    @media (min-width: 768px) {
      .col-sm-6 {
        height: calc(100vh - 160px) !important;
        overflow-y: scroll;
      }
    }

    .help {
      margin-top: 10px;
    }

    .save-scenario-button-container {
      margin-top: 10px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/breakeven-percentage-for-bluffing-the-turn/breakeven-percentage-for-bluffing-the-turn.vue